import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { orderBy } from 'lodash';
import categories from '../data/categories';
import moment from 'moment';
import CustomerItemAPI from "../api/customer-item/CustomerItemAPI";
import { useNotification } from "./NotificationContext";
import CustomerAPI from "../api/customer/CustomerAPI";


// setSelectedCustomerItemId => runtime selectie (klikken in lijst)
// setInitialSelectedCustomerItemId => init selectie (alleen deep link / route param)

const CustomerItemsContext = createContext({
    setCustomerId: () => {},
    customerId: null,
    customer: {},
    isLoadingCustomer: true,
    setSelectedCustomerItemId: () => {},
    selectedCustomerItemId: null,
    setInitialSelectedCustomerItemId: () => {},
    selectedCustomerItem: {},
    isLoadingItems: true,
    showWithArchived: false,
    filteredCustomerItems: [],
    categoryFilter: '',
    searchFilter: '',
    activeCategories: [],
    addCustomerItem: () => {},
    addNewCustomerItem: () => {},
    duplicateCustomerItem: () => {},
    updateCustomerItem: () => {},
    removeCustomerItem: () => {},
    toggleCustomerArchive: () => {},
    handleChangeCategoryFilter: () => {},
    handleChangeShowWithArchived: () => {},
    handleChangeSearchFilter: () => {},
    handleChangeSort: () => {},
    setCustomerItemFieldOnArchived: () => {},
});

const DEFAULT_EMPTY_CUSTOMER_ITEM = {
    id: null,
    categoryCode: '',
    title: '',
    subTitle: '',
    isArchived: false,
    previousSavedPasswords: [],
    category: { name: '' },
    customerItemAttachments: [],
};

const CustomerItemsProvider = function(props) {
    const [customerId, setCustomerId] = useState(null);
    const [customer, setCustomer] = useState({});
    const [isLoadingCustomer, setLoadingCustomer] = useState(true);
    const [isLoadingItems, setLoadingItems] = useState(true);

    const [customerItems, setCustomerItems] = useState([]);
    const [filteredCustomerItems, setFilteredCustomerItems] = useState([]);

    const [categoryFilter, setCategoryFilter] = useState('all_items');
    const [sortBy, setSortBy] = useState('titleAZ');
    const [searchFilter, setSearchFilter] = useState('');

    const [showWithArchived, setShowWithArchived] = useState(false);
    const [activeCategories, setActiveCategories] = useState([]);

    const [selectedCustomerItem, setSelectedCustomerItem] = useState({});
    const [selectedCustomerItemId, setSelectedCustomerItemId] = useState(null);

    const showNotification = useNotification();

    const customerIdRef = useRef(null);
    const selectedCustomerItemIdRef = useRef(null);

    // alleen voor init deep-link selection
    const pendingInitialSelectedCustomerItemIdRef = useRef(null);

    // deep-link setter (blijft dezelfde reference)
    const setInitialSelectedCustomerItemId = useCallback((id) => {
        pendingInitialSelectedCustomerItemIdRef.current = id;
        setSelectedCustomerItemId(id);
    }, []);

    // filters/sort toepassen wanneer data of filters veranderen
    useEffect(() => {
        if (customerItems.length === 0) {
            setFilteredCustomerItems([]);
            return;
        }
        fetchCustomerItemSearch().then(result => {
            applyFiltersAndSorting(customerItems, result);
        });
        // eslint-disable-next-line
    }, [customerItems, categoryFilter, searchFilter, sortBy]);

    // auto-select alleen bij init (als er nog niks gekozen is)
    useEffect(() => {
        if (!customerId) return;
        if (filteredCustomerItems.length === 0) return;

        if (selectedCustomerItemId === null) {
            setSelectedCustomerItemId(filteredCustomerItems[0].id);
        }
    }, [filteredCustomerItems, customerId, selectedCustomerItemId]);

    // Selected item laden (details)
    useEffect(() => {
        selectedCustomerItemIdRef.current = selectedCustomerItemId;

        if (selectedCustomerItemId === null) {
            setSelectedCustomerItem({});
            return;
        }

        const idStr = String(selectedCustomerItemId);
        if (idStr.includes('new_')) {
            setSelectedCustomerItem({});
            return;
        }

        // leeg details direct bij switch zodat je niet “oude” ziet hangen
        setSelectedCustomerItem({});
        fetchCustomerItemWithFields(selectedCustomerItemId);
        // eslint-disable-next-line
    }, [selectedCustomerItemId]);

    // Customer wissel: reset + reload
    useEffect(() => {
        // update ref (altijd eerst)
        customerIdRef.current = customerId;

        // reset state bij elke wijziging (ook als customerId null wordt)
        setSelectedCustomerItemId(null);
        setSelectedCustomerItem({});
        setCustomerItems([]);
        setFilteredCustomerItems([]);
        setActiveCategories([]);

        if (!customerId) return;

        callFetchCustomer({ preserveSelection: false });
        // eslint-disable-next-line
    }, [customerId]);

    // showWithArchived toggle: reload, selectie behouden
    useEffect(() => {
        if (!customerId) return;
        callFetchCustomer({ preserveSelection: true });
        // eslint-disable-next-line
    }, [showWithArchived]);

    function callFetchCustomer({ preserveSelection }) {
        setLoadingCustomer(true);
        setLoadingItems(true);

        CustomerAPI.fetchCustomer(customerId)
            .then(payload => {
                setCustomer(payload.data.data);
                setLoadingCustomer(false);
            })
            .catch(error => {
                console.log(error);
                showNotification('Er is iets misgegaan met het laden van de klant gegevens. Herlaad de pagina.');
                setLoadingCustomer(false);
            });

        fetchCustomerItems(preserveSelection);

        CustomerAPI.lastVisited(customerId)
            .then()
            .catch(error => () => console.log(error));
    }

    function fetchCustomerItems(preserveSelection) {
        CustomerItemAPI.fetchCustomerItems(customerId, showWithArchived)
            .then(payload => {
                const items = payload.data.data;

                handleChangeSearchFilter('');
                handleChangeCategoryFilter('all_items');
                handleChangeSort('titleAZ');

                const selectionToTry = preserveSelection
                    ? selectedCustomerItemIdRef.current
                    : pendingInitialSelectedCustomerItemIdRef.current;

                setInitialCustomerItems(items, selectionToTry);

                // consume pending deep link
                pendingInitialSelectedCustomerItemIdRef.current = null;

                setLoadingItems(false);
            })
            .catch(error => {
                console.log(error);
                showNotification('Er is iets misgegaan met het laden van de klant item gegevens. Herlaad de pagina.');
                setLoadingItems(false);
            });
    }

    const fetchCustomerItemSearch = async () => {
        if (searchFilter.length <= 1) return;
        try {
            const results = await CustomerAPI.fetchCustomerItemSearch(customerId, searchFilter);
            return results.data;
        } catch (error) {
            return null;
        }
    };

    function fetchCustomerItemWithFields(id) {
        const requestedId = String(id);
        const requestedCustomerId = String(customerIdRef.current);

        CustomerItemAPI.fetchCustomerItem(id, customerIdRef.current)
            .then(payload => {
                // Alleen setten als item én customer nog steeds kloppen
                if (
                    String(selectedCustomerItemIdRef.current) === requestedId &&
                    String(customerIdRef.current) === requestedCustomerId
                ) {
                    setSelectedCustomerItem(payload.data.data);
                }
            })
            .catch(error => {
                console.log(error);
                setSelectedCustomerItem({});
            });
    }

    function applyFiltersAndSorting(applyCustomerItems, customerItemFilterIds = null) {
        let filteredItems = null;

        if (!searchFilter || searchFilter.length <= 1) {
            filteredItems = applyCustomerItems;
        } else {
            filteredItems = applyCustomerItems.filter(item => {
                // Search in title and if something is found then return immediately
                if (item.title.toLowerCase().includes(searchFilter.toLowerCase())) return true;
                if (customerItemFilterIds && customerItemFilterIds.includes(item.id)) return true;
                return false;
            });
        }

        filteredItems = applyCategoryFilter(filteredItems);
        setFilteredCustomerItems(applySorting(filteredItems));
    }

    function applyCategoryFilter(items) {
        if (categoryFilter === 'all_items') return items;
        return items.filter(item => item.categoryCode === categoryFilter);
    }

    function applySorting(items) {
        switch (sortBy) {
            case 'titleAZ':
                return [...items].sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1));
            case 'titleZA':
                return [...items].sort((a, b) => (a.title.toLowerCase() < b.title.toLowerCase() ? 1 : -1));
            case 'lastUpdated':
                return orderBy(items, 'updatedAt', ['desc']);
            default:
                return items;
        }
    }

    function setInitialCustomerItems(payloadItems, selectedIdToTry) {
        defineActiveCategories(payloadItems);
        setCustomerItems(payloadItems);

        const filteredItems = applyCategoryFilter(payloadItems);
        setFilteredCustomerItems(applySorting(filteredItems));

        // behoud selectie als die bestaat en nog voorkomt in items
        if (selectedIdToTry && payloadItems.some(i => String(i.id) === String(selectedIdToTry))) {
            setSelectedCustomerItemId(selectedIdToTry);
        } else if (payloadItems.length > 0) {
            setSelectedCustomerItemId(payloadItems[0].id);
        } else {
            setSelectedCustomerItemId(null);
        }
    }

    function toggleCustomerArchive() {
        CustomerAPI.toggleArchive(customer.id, !customer.isArchived)
            .then(() => setCustomer({ ...customer, isArchived: !customer.isArchived }))
            .catch(() => {
                showNotification('Er is iets misgegaan met het archiveren van de klant. Herlaad de pagina.');
            });
    }

    function addCustomerItem(categoryCode, customerId, categoryFields) {
        const temporaryId = 'new_' + moment().unix();

        const newCustomerItem = {
            ...DEFAULT_EMPTY_CUSTOMER_ITEM,
            id: temporaryId,
            categoryCode,
            customerId,
            category: {
                name: categories.find(item => item.code === categoryCode).name,
            },
            customerItemFields: categoryFields.map((item, index) => ({
                id: 'new_' + index,
                customerItemId: null,
                categoryFieldId: item.id,
                label: item.label,
                typeCode: item.typeCode,
                value: '',
                valueEncrypted: '',
                order: item.order,
                isArchived: item.isArchived,
            })),
        };

        setSelectedCustomerItemId(temporaryId);
        return newCustomerItem;
    }

    function duplicateCustomerItem(duplicatedCustomerItem, customerId) {
        const temporaryId = 'new_' + moment().unix();

        return {
            id: temporaryId,
            categoryCode: duplicatedCustomerItem.categoryCode || '',
            customerId,
            title: duplicatedCustomerItem.title || '',
            subTitle: '',
            isArchived: duplicatedCustomerItem.isArchived || false,
            previousSavedPasswords: [],
            category: { name: duplicatedCustomerItem.category?.name || '' },
            customerItemAttachments: duplicatedCustomerItem.customerItemAttachments || [],
            customerItemFields: duplicatedCustomerItem.customerItemFields?.map((item, index) => ({
                id: 'new_' + index,
                customerItemId: null,
                categoryFieldId: item.categoryFieldId,
                label: item.label,
                typeCode: item.typeCode,
                value: item.value,
                valueEncrypted: item.valueEncrypted,
                order: item.order,
                isArchived: item.isArchived,
            })),
        };
    }

    function defineActiveCategories(items) {
        const categoryArray = [];
        items.forEach(ci => {
            if (!categoryArray.includes(ci.categoryCode)) categoryArray.push(ci.categoryCode);
        });
        setActiveCategories(categoryArray);
    }

    function handleChangeSearchFilter(value) {
        setSearchFilter(value);
    }
    function handleChangeCategoryFilter(value) {
        setCategoryFilter(value);
    }
    function handleChangeShowWithArchived(withArchived) {
        setShowWithArchived(withArchived);
    }
    function handleChangeSort(value) {
        setSortBy(value);
    }

    function addNewCustomerItem(newCustomerItem) {
        const newItems = [...customerItems, newCustomerItem];
        setCustomerItems(newItems);
        applyFiltersAndSorting(newItems, null);
        setSelectedCustomerItemId(newCustomerItem.id);
        defineActiveCategories(newItems);

        if (!String(newCustomerItem.id).includes('new_')) {
            fetchCustomerItemWithFields(newCustomerItem.id);
        }
    }

    function updateCustomerItem(payload) {
        const updatedItems = customerItems.map(ci => (ci.id === payload.id ? payload : ci));
        setCustomerItems(updatedItems);
        applyFiltersAndSorting(updatedItems, null);
        defineActiveCategories(updatedItems);

        if (
            String(payload.id) === String(selectedCustomerItemId) &&
            !String(payload.id).includes('new_')
        ) {
            fetchCustomerItemWithFields(payload.id);
        }
    }

    function removeCustomerItem(id) {
        const updatedItems = customerItems.map(ci => (ci.id === id ? { ...ci, isArchived: true } : ci));
        setCustomerItems(updatedItems);
        applyFiltersAndSorting(updatedItems, null);
        defineActiveCategories(updatedItems);

        if (String(id) === String(selectedCustomerItemId) && !String(id).includes('new_')) {
            fetchCustomerItemWithFields(id);
        }
    }

    function setCustomerItemFieldOnArchived(customerItemId, customerItemFieldId) {
        const updatedItems = customerItems.map(ci => {
            if (ci.id !== customerItemId) return ci;

            return {
                ...ci,
                customerItemFields: ci.customerItemFields?.map(f => {
                    if (f.id === customerItemFieldId) return { ...f, isArchived: true };
                    return f;
                }),
            };
        });

        setCustomerItems(updatedItems);

        if (
            String(customerItemId) === String(selectedCustomerItemId) &&
            !String(customerItemId).includes('new_')
        ) {
            fetchCustomerItemWithFields(customerItemId);
        }
    }

    // context value memoized (stabieler + minder rerenders)
    const value = useMemo(() => ({
        setCustomerId,
        customerId,
        customer,
        isLoadingCustomer,

        setSelectedCustomerItemId,
        selectedCustomerItemId,

        setInitialSelectedCustomerItemId,

        selectedCustomerItem,
        isLoadingItems,
        showWithArchived,
        filteredCustomerItems,
        categoryFilter,
        searchFilter,
        activeCategories,
        addCustomerItem,
        addNewCustomerItem,
        duplicateCustomerItem,
        updateCustomerItem,
        removeCustomerItem,
        toggleCustomerArchive,
        handleChangeCategoryFilter,
        handleChangeShowWithArchived,
        handleChangeSearchFilter,
        handleChangeSort,
        setCustomerItemFieldOnArchived,
    }), [
        customerId,
        customer,
        isLoadingCustomer,
        selectedCustomerItemId,
        setInitialSelectedCustomerItemId,
        selectedCustomerItem,
        isLoadingItems,
        showWithArchived,
        filteredCustomerItems,
        categoryFilter,
        searchFilter,
        activeCategories,
    ]);

    return (
        <CustomerItemsContext.Provider value={value}>
            {props.children}
        </CustomerItemsContext.Provider>
    );
};

const CustomerItemsConsumer = CustomerItemsContext.Consumer;

export { CustomerItemsProvider, CustomerItemsConsumer, CustomerItemsContext };
